import type { RenderContext, SectionProps } from "../context";
import { field, messageVars } from "../context";
import { waLink } from "../helpers";
import { Icon } from "../icons";
import { Container, MultiLine, STACK, SectionHeader, SectionShell, SiteLink, cx } from "../ui";

type Items = SectionProps<"faq">["node"]["content"]["items"];

function Accordion({ ctx, items, className }: { ctx: RenderContext; items: Items; className?: string }) {
  return (
    <div className={cx("baze-stagger divide-y divide-site-border border-y border-site-border", className)}>
      {items.map((item, i) => (
        <details key={i} className="group" open={i === 0 ? true : undefined}>
          <summary className="flex min-h-14 cursor-pointer list-none items-center justify-between gap-6 py-5 text-left [&::-webkit-details-marker]:hidden">
            <span className="text-base font-semibold text-site-text @3xl:text-lg" {...field(ctx, `content.items.${i}.question`)}>
              {item.question}
            </span>
            <span
              aria-hidden="true"
              className="grid size-9 shrink-0 place-items-center rounded-full border border-site-border text-site-text transition-transform duration-200 group-open:rotate-45 group-open:border-site-primary group-open:bg-site-primary group-open:text-site-on-primary motion-reduce:transition-none"
            >
              <Icon name="plus" className="size-4" />
            </span>
          </summary>
          <MultiLine ctx={ctx} text={item.answer} path={`content.items.${i}.answer`} className="max-w-2xl pb-6 leading-relaxed text-site-muted" />
        </details>
      ))}
    </div>
  );
}

function AskWhatsApp({ ctx }: { ctx: RenderContext }) {
  const href = waLink(ctx.site.contact.whatsapp, "Halo {{nama_usaha}}, saya mau tanya.", messageVars(ctx));
  if (!href) return null;
  return (
    <p className="mt-6 text-site-muted">
      Masih ada pertanyaan?{" "}
      <SiteLink ctx={ctx} href={href} whatsapp className="inline-flex min-h-11 items-center gap-1.5 font-semibold text-site-primary underline-offset-4 hover:underline">
        <Icon name="whatsapp" className="size-4" />
        Tanya via WhatsApp
      </SiteLink>
    </p>
  );
}

export function Faq({ node, ctx }: SectionProps<"faq">) {
  const c = node.content;
  if (node.variant === "two-column") {
    return (
      <SectionShell ctx={ctx} node={node}>
        <Container>
          <div className="grid gap-10 @4xl:grid-cols-[1fr_1.5fr] @4xl:gap-16">
            <div className="@4xl:sticky @4xl:top-[calc(7rem+var(--baze-sticky-top,0px))] @4xl:self-start">
              <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title}>
                <AskWhatsApp ctx={ctx} />
              </SectionHeader>
            </div>
            <Accordion ctx={ctx} items={c.items} />
          </div>
        </Container>
      </SectionShell>
    );
  }
  return (
    <SectionShell ctx={ctx} node={node}>
      <Container size="narrow">
        <SectionHeader ctx={ctx} node={node} eyebrow={c.eyebrow} title={c.title} align="center" />
        <Accordion ctx={ctx} items={c.items} className={STACK} />
        <div className="text-center">
          <AskWhatsApp ctx={ctx} />
        </div>
      </Container>
    </SectionShell>
  );
}
